import { useEffect, useMemo, useState } from "react";
import type { ClipboardEvent } from "react";
import { Avatar, Button, Input, Modal, Select } from "antd";
import RoleSelect from "@/components/Admin/RoleSelect";
import {
  CloseOutlined,
  InfoCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";

import {
  adminAccountTypeOptions,
  type AdminAccountType,
  type AdminStudyOption,
  type AdminStudyPermissionAccess,
} from "./adminUsersAccessData";
import InfoTooltip from "@/components/UI/InfoTooltip";
import {
  formatRoleLabel,
  getRoleDotColor,
} from "./adminRolesPermissionsMappers";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MODAL_HEIGHT = 710;

const SELECT_TRIGGER_CLASS =
  "[&_.ant-select-selector]:!h-[35px] [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!border-[rgba(0,0,0,0.12)] [&_.ant-select-selector]:!px-2 [&_.ant-select-selector]:!items-center";

const SHARED_STUDY_ASSIGNMENT_TOOLTIP =
  "Apply the same study and role to every invited user with user access before sending invites. Administrator invites do not require a study assignment.";

export type BatchInvitePayload = {
  invites: Array<{ email: string; accountType: AdminAccountType }>;
  sharedStudyAssignment?: {
    studyId: string;
    access: AdminStudyPermissionAccess;
  };
};

export type BatchInviteSubmitResult = {
  failedEmails?: string[];
};

type BatchInviteUsersModalProps = {
  open: boolean;
  existingEmails: string[];
  studyOptions: AdminStudyOption[];
  isSubmitting?: boolean;
  submitError?: string | null;
  onCancel: () => void;
  onSubmit: (
    payload: BatchInvitePayload,
  ) => BatchInviteSubmitResult | Promise<BatchInviteSubmitResult>;
};

type InviteEntry = {
  id: string;
  email: string;
  accountType: AdminAccountType;
};

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

function splitEmails(rawValue: string) {
  return rawValue
    .split(/[\s,]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function buildAccessOption(access: AdminStudyPermissionAccess) {
  return {
    value: access,
    title: formatRoleLabel(access),
    label: (
      <span className="flex items-center gap-2">
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: getRoleDotColor(access) }}
        />
        <span className="text-xs font-medium tracking-wide">
          {formatRoleLabel(access)}
        </span>
      </span>
    ),
  };
}

function buildAccountTypeLabel(accountType: AdminAccountType) {
  return accountType === "ADMIN" ? "Administrator" : "User";
}

function buildInviteEntryId(email: string, index: number) {
  return `invite-${normalizeEmail(email).replace(/[^a-z0-9]+/g, "-")}-${index}`;
}

function findExistingEmailMatch(email: string, existingEmails: Set<string>) {
  const normalizedEmail = normalizeEmail(email);

  return existingEmails.has(normalizedEmail);
}

function InviteList({
  invitees,
  onAccountTypeChange,
  onRemove,
}: {
  invitees: InviteEntry[];
  onAccountTypeChange: (
    inviteeId: string,
    accountType: AdminAccountType,
  ) => void;
  onRemove: (inviteeId: string) => void;
}) {
  if (invitees.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-[rgba(0,0,0,0.02)] p-3">
      {invitees.map((invitee) => (
        <div
          key={invitee.id}
          className="flex items-center justify-between gap-3"
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Avatar
              size={24}
              className="flex-shrink-0 bg-[#e8eef0] text-[#556c75]"
              icon={<UserOutlined />}
            />
            <span className="truncate text-sm text-[rgba(0,0,0,0.85)]">
              {invitee.email}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Select
              virtual={false}
              aria-label={`Account type for ${invitee.email}`}
              className={`min-w-[172px] ${SELECT_TRIGGER_CLASS}`}
              value={invitee.accountType}
              onChange={(accountType: AdminAccountType) => {
                onAccountTypeChange(invitee.id, accountType);
              }}
              options={adminAccountTypeOptions.map((accountType) => ({
                value: accountType,
                label: buildAccountTypeLabel(accountType),
              }))}
            />

            <button
              type="button"
              aria-label={`Remove ${invitee.email}`}
              className="flex h-5 w-5 items-center justify-center rounded p-1 text-[rgba(0,0,0,0.45)] transition-colors hover:bg-[rgba(0,0,0,0.06)] hover:text-[#20353e]"
              onClick={() => onRemove(invitee.id)}
            >
              <CloseOutlined className="text-xs" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function BatchInviteUsersModal({
  open,
  existingEmails,
  studyOptions,
  isSubmitting = false,
  submitError = null,
  onCancel,
  onSubmit,
}: BatchInviteUsersModalProps) {
  const [emailInput, setEmailInput] = useState("");
  const [invitees, setInvitees] = useState<InviteEntry[]>([]);
  const [inputError, setInputError] = useState<string | null>(null);
  const [sharedStudyId, setSharedStudyId] = useState<string>("");
  const [sharedAccess, setSharedAccess] = useState<
    AdminStudyPermissionAccess | ""
  >("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setEmailInput("");
    setInvitees([]);
    setInputError(null);
    setSharedStudyId("");
    setSharedAccess("");
  }, [open]);

  const existingEmailSet = useMemo(
    () => new Set(existingEmails.map(normalizeEmail)),
    [existingEmails],
  );

  const inviteEmailSet = useMemo(
    () => new Set(invitees.map((invitee) => normalizeEmail(invitee.email))),
    [invitees],
  );

  const userInvitees = useMemo(
    () => invitees.filter((invitee) => invitee.accountType === "USER"),
    [invitees],
  );
  const adminInvitees = useMemo(
    () => invitees.filter((invitee) => invitee.accountType === "ADMIN"),
    [invitees],
  );
  const hasUserInvitees = userInvitees.length > 0;
  const hasAdminInvitees = adminInvitees.length > 0;
  const hasAvailableStudyOptions = studyOptions.length > 0;
  const sharedStudyRoleOptions = useMemo(
    () => studyOptions.find((study) => study.id === sharedStudyId)?.roles ?? [],
    [studyOptions, sharedStudyId],
  );
  const canSubmit =
    invitees.length > 0 &&
    (!hasUserInvitees || Boolean(sharedStudyId && sharedAccess));

  const addEmailsFromInput = (rawValue: string) => {
    const tokens = splitEmails(rawValue);

    if (tokens.length === 0) {
      setInputError("Enter one or more email addresses.");
      return false;
    }

    const nextInvitees = [...invitees];
    const seenEmails = new Set(inviteEmailSet);
    const errors: string[] = [];
    let addedCount = 0;

    tokens.forEach((token) => {
      const normalized = normalizeEmail(token);

      if (!EMAIL_REGEX.test(token)) {
        errors.push(`"${token}" is not a valid email address.`);
        return;
      }

      if (findExistingEmailMatch(token, existingEmailSet)) {
        errors.push(`"${normalized}" already exists in Users & Access.`);
        return;
      }

      if (seenEmails.has(normalized)) {
        errors.push(`"${normalized}" is already in this invite batch.`);
        return;
      }

      seenEmails.add(normalized);
      nextInvitees.push({
        id: buildInviteEntryId(token, nextInvitees.length),
        email: normalized,
        accountType: "USER",
      });
      addedCount += 1;
    });

    if (addedCount > 0) {
      setInvitees(nextInvitees);
      setEmailInput("");
    }

    setInputError(errors.length > 0 ? errors[0] : null);

    return addedCount > 0;
  };

  const handleAdd = () => {
    addEmailsFromInput(emailInput);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData("text");

    if (!pastedText.includes(",") && !pastedText.includes("\n")) {
      return;
    }

    event.preventDefault();
    addEmailsFromInput(pastedText);
  };

  const handleAccountTypeChange = (
    inviteeId: string,
    accountType: AdminAccountType,
  ) => {
    setInvitees((current) =>
      current.map((invitee) =>
        invitee.id === inviteeId ? { ...invitee, accountType } : invitee,
      ),
    );
  };

  const handleRemoveInvitee = (inviteeId: string) => {
    setInvitees((current) =>
      current.filter((invitee) => invitee.id !== inviteeId),
    );
  };

  const handleClearSelection = () => {
    setSharedStudyId("");
    setSharedAccess("");
    setInputError(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      if (invitees.length === 0) {
        setInputError("Add at least one email before sending invites.");
        return;
      }

      if (hasUserInvitees && (!sharedStudyId || !sharedAccess)) {
        setInputError(
          "Assign both a study and study access role for invited users.",
        );
      }

      return;
    }

    const payload: BatchInvitePayload = {
      invites: invitees.map((invitee) => ({
        email: invitee.email,
        accountType: invitee.accountType,
      })),
    };

    if (hasUserInvitees && sharedStudyId && sharedAccess) {
      payload.sharedStudyAssignment = {
        studyId: sharedStudyId,
        access: sharedAccess,
      };
    }

    const result = await onSubmit(payload);

    if (result.failedEmails?.length) {
      const failedEmails = new Set(result.failedEmails.map(normalizeEmail));
      setInvitees((current) =>
        current.filter((invitee) =>
          failedEmails.has(normalizeEmail(invitee.email)),
        ),
      );
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      centered
      width={840}
      height={MODAL_HEIGHT}
      title={
        <span className="text-[18px] font-medium leading-[normal] text-[rgba(0,0,0,0.85)]">
          Invite new user(s)
        </span>
      }
      closeIcon={<CloseOutlined className="text-sm text-[rgba(0,0,0,0.85)]" />}
      styles={{
        container: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          overflow: "hidden",
        },
        header: {
          margin: 0,
          padding: "12px 12px 11px",
          flex: "0 0 auto",
        },
        body: {
          padding: 0,
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
        },
        footer: {
          margin: 0,
          padding: 0,
          flex: "0 0 auto",
        },
      }}
      footer={
        <div className="flex items-center justify-between border-t border-[rgba(0,0,0,0.06)] p-3">
          <Button
            type="default"
            className="h-8 rounded border-[rgba(0,0,0,0.45)] px-3 text-[14px] font-medium text-[#2b4752] shadow-none"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="h-8 rounded px-3 text-[14px] font-medium"
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
            onClick={() => void handleSubmit()}
          >
            Submit & send
          </Button>
        </div>
      }
      data-testid="batch-invite-users-modal"
      destroyOnHidden
    >
      <div className="flex h-full flex-col gap-5 p-4">
        <section className="flex flex-col gap-3">
          <span className="text-[16px] font-medium leading-[normal] tracking-[-0.16px] text-[#2b4752]">
            Users
          </span>

          <div className="flex items-center gap-2">
            <Input
              aria-label="Invite user email input"
              className="h-8 rounded border-[rgba(0,0,0,0.12)] bg-white px-2 text-sm text-[#394c54]"
              placeholder="Enter emails separated by commas then hit enter or click add"
              value={emailInput}
              onChange={(event) => {
                setEmailInput(event.target.value);
                setInputError(null);
              }}
              onPressEnter={handleAdd}
              onPaste={handlePaste}
            />
            <Button
              className="h-8 rounded border-[rgba(0,0,0,0.45)] px-3 text-[14px] font-medium text-[#2b4752] shadow-none"
              onClick={handleAdd}
            >
              Add
            </Button>
          </div>

          <InviteList
            invitees={userInvitees}
            onAccountTypeChange={handleAccountTypeChange}
            onRemove={handleRemoveInvitee}
          />
        </section>

        {inputError ? (
          <div role="alert" className="text-sm text-[#980000]">
            {inputError}
          </div>
        ) : null}

        {submitError ? (
          <div role="alert" className="text-sm text-[#980000]">
            {submitError}
          </div>
        ) : null}

        {hasUserInvitees ? (
          <section className="flex flex-col gap-3">
            <div className="flex h-6 items-center justify-between">
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium text-[#20353e]">
                  Assign invited users to study with role
                </span>
                <InfoTooltip
                  title={SHARED_STUDY_ASSIGNMENT_TOOLTIP}
                  fill="rgba(0,0,0,0.45)"
                  data-testid="batch-invite-shared-study-assignment-tooltip"
                />
              </div>

              <Button
                className="h-6 rounded border-[rgba(0,0,0,0.12)] px-2 text-[14px] font-normal text-[#2b4752] shadow-none"
                onClick={handleClearSelection}
              >
                Clear selection
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex flex-1 flex-col gap-1">
                <span className="text-[12px] font-semibold uppercase tracking-[-0.12px] text-[rgba(0,0,0,0.45)]">
                  Study
                </span>
                <Select
                  virtual={false}
                  aria-label="Shared study"
                  placeholder={
                    hasAvailableStudyOptions
                      ? "Select a study"
                      : "No managed studies available"
                  }
                  className={`w-full ${SELECT_TRIGGER_CLASS}`}
                  value={sharedStudyId || undefined}
                  onChange={(value: string) => {
                    setSharedStudyId(value);
                    const nextRoles =
                      studyOptions.find((study) => study.id === value)?.roles ??
                      [];
                    setSharedAccess((currentAccess) =>
                      currentAccess && nextRoles.includes(currentAccess)
                        ? currentAccess
                        : "",
                    );
                    setInputError(null);
                  }}
                  options={studyOptions.map((study) => ({
                    value: study.id,
                    label: study.label,
                  }))}
                  disabled={!hasAvailableStudyOptions}
                />
              </label>

              <label className="flex flex-1 flex-col gap-1">
                <span className="text-[12px] font-semibold uppercase tracking-[-0.12px] text-[rgba(0,0,0,0.45)]">
                  Study access role
                </span>
                <RoleSelect
                  virtual={false}
                  aria-label="Shared study access role"
                  placeholder="Select a study role"
                  className={`w-full ${SELECT_TRIGGER_CLASS} ${
                    sharedStudyId
                      ? ""
                      : "[&_.ant-select-selector]:!bg-[rgba(0,0,0,0.04)] [&_.ant-select-selection-placeholder]:!text-[rgba(0,0,0,0.25)]"
                  }`}
                  value={sharedAccess || undefined}
                  onChange={(value: AdminStudyPermissionAccess) => {
                    setSharedAccess(value);
                    setInputError(null);
                  }}
                  options={sharedStudyRoleOptions.map(buildAccessOption)}
                  disabled={!hasAvailableStudyOptions || !sharedStudyId}
                />
              </label>
            </div>
          </section>
        ) : null}

        {hasAdminInvitees ? (
          <>
            <div className="h-px w-full bg-[rgba(0,0,0,0.06)]" />

            <section className="flex flex-col gap-3">
              <span className="text-[16px] font-medium leading-[normal] tracking-[-0.16px] text-[#2b4752]">
                Admins
              </span>

              <InviteList
                invitees={adminInvitees}
                onAccountTypeChange={handleAccountTypeChange}
                onRemove={handleRemoveInvitee}
              />

              <div className="flex items-center gap-2.5 rounded-lg border border-[rgba(0,0,0,0.04)] bg-[rgba(236,250,255,0.6)] px-4 py-3">
                <InfoCircleOutlined className="text-[16px] text-[#0c98d0]" />
                <p className="flex-1 text-sm text-[#20353e]">
                  Admins can manage people and permissions. They do not have
                  data access by default. To grant study access, go to Roles
                  &amp; Permissions.
                </p>
              </div>
            </section>
          </>
        ) : (
          <div className="flex-1" />
        )}
      </div>
    </Modal>
  );
}
